import { z } from 'zod';
import { dateSchema, integerSchema, priceSchema } from '../../validation';
import { imageWithUploadSchema } from '../../common';
import {
  financingFleetTierSchema,
  financingNeedSchema,
  financingRevenueTierSchema,
} from './financing.enums';

/**
 * Org-level financing profile. Carriers fill this once, inline on
 * their first apply form; subsequent applications snapshot it into
 * `FinancingApplication.profileSnapshot` and don't re-prompt.
 *
 * Lives next to `Org.legal` as an optional sub-block so the field
 * is fully additive — no migration logic beyond the schema-version
 * marker (see `apps/backend/migrations/.../-create-financing.ts`).
 */
export const orgFinancingProfileObjectSchema = z.object({
  fleetTier: financingFleetTierSchema,
  annualRevenueTier: financingRevenueTierSchema,
  /**
   * Optional, multi-valued. Carriers can hint at other financing
   * needs (trucks, working capital, fuel, etc.) on top of this
   * invoice — purely informational for the lender packet, so an
   * empty array is allowed. Field renamed from `financingPurposes`
   * to `financingNeeds` in the MVP cleanup (2026-06).
   */
  financingNeeds: z.array(financingNeedSchema),
  ownsAllTrucks: z.boolean(),
  /** REQUIRED iff `ownsAllTrucks=false`; the form enforces this client-side. */
  trucksOwnedCount: integerSchema.optional(),
  hasLoans: z.boolean(),
  /** REQUIRED iff `hasLoans=true`; the form enforces this client-side. */
  monthlyFinancingPayment: priceSchema.optional(),

  /**
   * Self-reported profitability flag. Optional because the
   * carrier may legitimately skip it ("don't want to say"); when
   * present it becomes part of the underwriting packet. We do
   * NOT attempt to verify against uploaded statements — that
   * stays a lender concern.
   */
  isProfitable: z.boolean().optional(),

  /**
   * Optional uploaded financial statements (P&L, balance sheet,
   * etc.). Highly recommended; a present-and-non-empty array
   * tends to unlock larger advance offers. Stored as the same
   * `imageWithUpload` shape used by CR / tax / invoice docs so
   * we can reuse the upload + preview pipeline (and keep the
   * file in PDF / JPEG / PNG with consistent CDN URLs).
   */
  financialStatements: z.array(imageWithUploadSchema).optional(),

  /** Stamped server-side every time the profile is written. */
  updatedAt: dateSchema,
});

type OrgFinancingProfileRefineInput = {
  ownsAllTrucks: boolean;
  trucksOwnedCount?: number;
};

const refineOrgFinancingProfile = (
  data: OrgFinancingProfileRefineInput,
  ctx: z.RefinementCtx,
) => {
  if (!data.ownsAllTrucks && data.trucksOwnedCount == null) {
    ctx.addIssue({
      code: 'custom',
      path: ['trucksOwnedCount'],
      message: 'Required when some trucks are financed',
    });
  }
};

export const orgFinancingProfileSchema =
  orgFinancingProfileObjectSchema.superRefine(refineOrgFinancingProfile);

/** Client write shape — same fields, no server-stamped `updatedAt`. */
export const orgFinancingProfileUpsertSchema = orgFinancingProfileObjectSchema
  .omit({ updatedAt: true })
  .superRefine(refineOrgFinancingProfile);

export type OrgFinancingProfile = z.infer<typeof orgFinancingProfileSchema>;
export type OrgFinancingProfileUpsert = z.infer<typeof orgFinancingProfileUpsertSchema>;
